import React, { useState, useEffect, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";

interface BlockedIp {
  id: number;
  siteId: number;
  ipOrRange: string;
  type: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  isActive: boolean;
}

interface ListResponse {
  entries: BlockedIp[];
  activeCount: number;
  maxBlockedIps: number;
}

type SortKey = "ipOrRange" | "type" | "createdBy" | "createdAt" | "expiresAt" | "isActive";
type SortDir = "asc" | "desc";

export default function IpBlockingPanel() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const { subscription } = useSubscriptions();
  const [entries, setEntries] = useState<BlockedIp[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maxBlockedIps, setMaxBlockedIps] = useState(50);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const pageSize = 50;

  // Admin app users are already authorized admins/managers
  const canManage = true;

  const fetchData = useCallback(async () => {
    try {
      const res = await api.messaging.get("BlockedIp");
      if (!res.ok) {
        if (res.status === 400) {
          setLoading(false);
          return; // feature not enabled
        }
        throw new Error("Failed to load");
      }
      const data: ListResponse = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setActiveCount(data.activeCount ?? 0);
      setMaxBlockedIps(data.maxBlockedIps ?? 50);
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to load blocked IPs");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, toastAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtering + sorting
  const filteredEntries = useMemo(() => {
    let result = [...entries];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.ipOrRange.toLowerCase().includes(lower) ||
          (e.reason?.toLowerCase().includes(lower) ?? false) ||
          e.createdBy.toLowerCase().includes(lower)
      );
    }

    if (filterType !== "all") {
      result = result.filter((e) => e.type === filterType);
    }

    if (filterStatus === "active") {
      result = result.filter((e) => e.isActive);
    } else if (filterStatus === "inactive") {
      result = result.filter((e) => !e.isActive);
    }

    result.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];
      if (sortKey === "createdAt" || sortKey === "expiresAt") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [entries, search, filterType, filterStatus, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const pagedEntries = filteredEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleToggleActive = async (entry: BlockedIp) => {
    try {
      const res = await api.messaging.put(`BlockedIp/${entry.id}`, {
        isActive: !entry.isActive,
      });
      if (!res.ok) {
        const data = await res.json();
        toastAlert.displayToast(Severity.Error, data.message ?? "Update failed");
        return;
      }
      toastAlert.displayToast(
        Severity.Success,
        entry.isActive ? "IP unblocked" : "IP re-blocked"
      );
      fetchData();
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to update");
    }
  };

  const handleDelete = async (entry: BlockedIp) => {
    if (!confirm(`Remove block on ${entry.ipOrRange}?`)) return;
    try {
      const res = await api.messaging.delete(`BlockedIp/${entry.id}`);
      if (!res.ok) {
        const data = await res.json();
        toastAlert.displayToast(Severity.Error, data.message ?? "Delete failed");
        return;
      }
      toastAlert.displayToast(Severity.Success, "Blocked IP removed");
      fetchData();
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilterType("all");
    setFilterStatus("all");
    setCurrentPage(1);
  };

  if (!subscription?.enableIpBlocking) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">IP Blocking</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800">
            IP blocking is not enabled for your site. Contact your administrator or upgrade your plan to enable this feature.
          </p>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">IP Blocking</h2>
        <p className="text-slate-500">You do not have permission to manage IP blocking.</p>
      </div>
    );
  }

  const typeBadge = (type: string) => {
    const colors: Record<string, string> = {
      single: "bg-green-100 text-green-700",
      cidr: "bg-blue-100 text-blue-700",
      range: "bg-purple-100 text-purple-700",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${colors[type] ?? "bg-slate-100 text-slate-700"}`}>
        {type}
      </span>
    );
  };

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">IP Blocking</h2>
          <p className="text-sm text-slate-500 mt-1">
            {activeCount} / {maxBlockedIps} blocked IPs
          </p>
        </div>
        <button
          className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
          onClick={() => setShowAddModal(true)}
          disabled={activeCount >= maxBlockedIps}
        >
          + Block IP
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search IP, reason, created by..."
          className="px-3 py-1.5 border border-slate-300 rounded text-sm w-64"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          aria-label="Search blocked IPs"
        />
        <label htmlFor="filterType" className="sr-only">Filter by type</label>
        <select
          id="filterType"
          className="px-3 py-1.5 border border-slate-300 rounded text-sm"
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Types</option>
          <option value="single">Single IP</option>
          <option value="cidr">CIDR</option>
          <option value="range">Range</option>
        </select>
        <label htmlFor="filterStatus" className="sr-only">Filter by status</label>
        <select
          id="filterStatus"
          className="px-3 py-1.5 border border-slate-300 rounded text-sm"
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          onClick={clearFilters}
        >
          Clear Filters
        </button>
        <span className="text-xs text-slate-400 ml-auto">
          {filteredEntries.length} of {entries.length} entries
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("ipOrRange")}>
                IP/Range{sortArrow("ipOrRange")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("type")}>
                Type{sortArrow("type")}
              </th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("createdBy")}>
                Created By{sortArrow("createdBy")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("createdAt")}>
                Created{sortArrow("createdAt")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("expiresAt")}>
                Expires{sortArrow("expiresAt")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("isActive")}>
                Status{sortArrow("isActive")}
              </th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : pagedEntries.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No blocked IPs found</td></tr>
            ) : (
              pagedEntries.map((entry) => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-mono text-xs">{entry.ipOrRange}</td>
                  <td className="px-3 py-1.5">{typeBadge(entry.type)}</td>
                  <td className="px-3 py-1.5 max-w-[200px] truncate text-xs" title={entry.reason ?? ""}>
                    {entry.reason || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{entry.createdBy}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {entry.expiresAt ? new Date(entry.expiresAt).toLocaleDateString() : "Never"}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      entry.isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {entry.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex gap-1">
                      <button
                        className={`px-2 py-1 rounded text-[11px] ${
                          entry.isActive
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                        }`}
                        onClick={() => handleToggleActive(entry)}
                        aria-label={entry.isActive ? `Deactivate block on ${entry.ipOrRange}` : `Reactivate block on ${entry.ipOrRange}`}
                      >
                        {entry.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="px-2 py-1 rounded text-[11px] bg-red-100 text-red-700 hover:bg-red-200"
                        onClick={() => handleDelete(entry)}
                        aria-label={`Delete block on ${entry.ipOrRange}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddBlockedIpModal
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); fetchData(); }}
          api={api}
          toastAlert={toastAlert}
        />
      )}
    </div>
  );
}

function AddBlockedIpModal({
  onClose,
  onAdded,
  api,
  toastAlert,
}: {
  onClose: () => void;
  onAdded: () => void;
  api: ReturnType<typeof useApi>;
  toastAlert: ReturnType<typeof useToastAlert>;
}) {
  const [ipOrRange, setIpOrRange] = useState("");
  const [type, setType] = useState("single");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body: any = { ipOrRange: ipOrRange.trim(), type, reason: reason.trim() || undefined };
      if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString();

      const res = await api.messaging.post("BlockedIp", body);
      if (!res.ok) {
        const data = await res.json();
        toastAlert.displayToast(Severity.Error, data.message ?? "Failed to add blocked IP");
        return;
      }
      toastAlert.displayToast(Severity.Success, "IP blocked successfully");
      onAdded();
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to add blocked IP");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Block IP address"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Block IP Address</h3>
          <button onClick={onClose} aria-label="Close dialog" className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="ipType" className="block text-sm font-medium text-slate-700 mb-1">
              Type
            </label>
            <select
              id="ipType"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="single">Single IP</option>
              <option value="cidr">CIDR Range (e.g. 192.168.1.0/24)</option>
              <option value="range">IP Range (e.g. 10.0.0.1-10.0.0.100)</option>
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="ipOrRange" className="block text-sm font-medium text-slate-700 mb-1">
              IP Address or Range
            </label>
            <input
              id="ipOrRange"
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
              value={ipOrRange}
              onChange={(e) => setIpOrRange(e.target.value)}
              placeholder={
                type === "single" ? "192.168.1.100" :
                type === "cidr" ? "192.168.1.0/24" :
                "10.0.0.1-10.0.0.100"
              }
              required
              autoFocus
            />
          </div>
          <div className="mb-3">
            <label htmlFor="reason" className="block text-sm font-medium text-slate-700 mb-1">
              Reason (optional)
            </label>
            <input
              id="reason"
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Spam bot, abusive visitor..."
              maxLength={500}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="expiresAt" className="block text-sm font-medium text-slate-700 mb-1">
              Expires (optional — leave empty for permanent)
            </label>
            <input
              id="expiresAt"
              type="datetime-local"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
              disabled={submitting || !ipOrRange.trim()}
            >
              {submitting ? "Blocking..." : "Block IP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
